/**
 * Asset Naming Utility
 * ====================
 *
 * Industry-aware asset name generation for consistent naming across the application.
 * Automatically adapts asset names based on the current industry context.
 */

import { getIndustry, getTerminology, loadIndustryFromStorage } from '@/services/industryAdapter'
import { generateSIMDisplayName } from './simCardGenerator'

// Initialize industry from localStorage on module load
loadIndustryFromStorage()

/**
 * Generate industry-specific asset name
 *
 * @param assetId - Asset ID
 * @param index - Asset index (optional, for numbered assets)
 * @returns Industry-specific asset name
 *
 * @example
 * // In IoT/Telecom industry
 * generateAssetName('F67503', 1) // Returns "SIM 123456" (using ICCID last 6 digits)
 *
 * // In Healthcare industry
 * generateAssetName('F67503', 1) // Returns "Device F67503"
 *
 * // In Oil & Gas industry
 * generateAssetName('F67503', 1) // Returns "Equipment F67503"
 */
export function generateAssetName(assetId: string, index?: number): string {
  const industryId = getIndustry()
  const terminology = getTerminology(industryId)

  // IoT/Telecom uses proper SIM ICCID format
  if (industryId === 'iot_telecom') {
    return generateSIMDisplayName(assetId, 'US')
  }

  // Industry-specific naming patterns for other industries
  const namingPatterns: Record<string, (id: string, idx?: number) => string> = {
    healthcare: (id, idx) => `Device ${id}`,
    financial: (id, idx) => `System ${id}`,
    transportation: (id, idx) => `Vehicle ${id}`,
    energy: (id, idx) => `Turbine ${id}`,
    datacenter: (id, idx) => `Server ${id}`,
    aerospace: (id, idx) => `Aircraft System ${id}`,
    // Default pattern for industrial industries (oil_gas, manufacturing, mining, etc.)
    default: (id, idx) => {
      // Capitalize first letter of asset terminology
      const assetTerm = terminology.asset.charAt(0).toUpperCase() + terminology.asset.slice(1)
      return `${assetTerm} ${id}`
    }
  }

  const nameGenerator = namingPatterns[industryId] || namingPatterns.default
  return nameGenerator(assetId, index)
}

/**
 * Generate industry-specific asset type
 *
 * @returns Industry-specific asset type
 *
 * @example
 * // In IoT/Telecom industry
 * getAssetType() // Returns "sim_card"
 *
 * // In Healthcare industry
 * getAssetType() // Returns "device"
 */
export function getAssetType(): string {
  const industryId = getIndustry()
  const terminology = getTerminology(industryId)

  const typeMapping: Record<string, string> = {
    iot_telecom: 'sim_card',
    healthcare: 'medical_device',
    financial: 'system',
    transportation: 'vehicle',
    energy: 'turbine',
    datacenter: 'server',
    aerospace: 'aircraft_system',
    // Default: use terminology
    default: terminology.asset.toLowerCase().replace(/\s+/g, '_')
  }

  return typeMapping[industryId] || typeMapping.default
}
